
import { ComponentType } from '@angular/cdk/portal';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormGroup, ValidationErrors } from '@angular/forms';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { MatSnackBar, MatSnackBarConfig } from '@angular/material/snack-bar';
import { NavigationStart, Router } from '@angular/router';
import { ConfirmTermsDialogComponent } from '@carm/shared/components/confirm-terms-dialog/confirm-terms-dialog.component';
import { ModalData, TermOptions } from '@carm/shared/components/modal/modal-data-model';
import { ModalComponent } from '@carm/shared/components/modal/modal.component';
import {
  getFormValidationErrors,
  getMessageByCode,
  UpdateErrorMapInFormGroup,
} from '@carm/_core/helper-functions/error-mapper-functions';
import { is502Error } from '@carm/_core/logger/models/error.model';
import { NotificationService } from '@carm/_core/notification-mgt/facade/notifcation.service';
import { ISnackbarData, NotificationTypes } from '@carm/_core/notification-mgt/models/notification.model';
import { TranslateService } from '@ngx-translate/core';
import { Observable } from 'rxjs';
import { map, takeUntil } from 'rxjs/operators';
import { AppInjector } from './app.injector';
import { BaseComponent } from './base.component';
import { BusinessRuleError, IMappedError } from './models';
import { IError, IErrorCode } from './models/error.model';
import { AlertService } from '@carm/_core/alerts/services/alert.service';

@Component({ template: '' })
export class BasePageComponent extends BaseComponent implements OnInit, OnDestroy {
  notificationTypes = NotificationTypes;
  protected notificationService: NotificationService;
  protected translateService: TranslateService;
  protected baseRouter: Router;
  protected dialog: MatDialog;
  protected alertService: AlertService;
  public navigationButtonClicked;
  public backButtonPopupConfig: ModalData;
  public showLeavingWarning = false;
  private snackBar: MatSnackBar;

  constructor(
  ) {
    super();
    const injector = AppInjector.getInjector();
    this.notificationService = injector.get(NotificationService);
    this.translateService = injector.get(TranslateService);
    this.baseRouter = injector.get(Router);
    this.dialog = injector.get(MatDialog);
    this.snackBar = injector.get(MatSnackBar);
    this.alertService = injector.get(AlertService);

    this.baseRouter.events
      .pipe(takeUntil(this.destroy$)).subscribe(event => {
        if (event instanceof NavigationStart) {
          this.alertService.setAlertKey(null);
        }
      });
  }

  ngOnInit(): void {

  }

  setBackButtonPopup(title: string, message: string, linkDescription: string, link: string, showLeaveButton = false) {
    this.baseRouter.events
    .pipe(takeUntil(this.destroy$))
    .subscribe((event: any) => {
      if (event instanceof NavigationStart ) {
        if (event.navigationTrigger === 'popstate') {
          this.navigationButtonClicked = true;

          this.backButtonPopupConfig = {
            title: this.translateService.instant(title),
            contentParagraphs: [ this.translateService.instant(message) ],
            linkDescription: this.translateService.instant(linkDescription),
            link: link,
            buttons: [
              {
                id: 'cancel',
                label: this.translateService.instant('button.cancel'),
                class: undefined,
              },
            ],
          };

          if(showLeaveButton){
            this.backButtonPopupConfig.buttons.push( { id: 'leave', label: this.translateService.instant('button.leave'), class: undefined});
          }

        } else {
          this.navigationButtonClicked = false;
          }
        }
      }
    );
  }

  setPageErrorMessage(message: string, title?: string, scrollToErrorAlert?: boolean) {
    this.notificationService.setPageErrorMessage(message, title);

    if (scrollToErrorAlert) {
      setTimeout(() => {
        const pageTitles = document.querySelectorAll('.carm-alerts');
        if (pageTitles.length > 0) {
          const alertElement = pageTitles[0] as HTMLElement;
          alertElement.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    }
  }

  setPageSuccessMessage(message: string, title?: string) {
    this.notificationService.setPageSuccessMessage(message, title);
  }

  setPageWarningMessage(message: string, title?: string) {
    this.notificationService.setPageWarningMessage(message, title);
  }

  setPageInfoMessage(message: string, title?: string) {
    this.notificationService.setPageInfoMessage(message, title);
  }

  clearMessages() {
    this.notificationService.clearNotification();
    this.notificationService.clearPageNotification();
    this.notificationService.clearFormErrorNotification();
  }

  ngOnDestroy(): void {
    this.notificationService.clearNotification();
    this.notificationService.clearPageNotification();
    this.notificationService.clearFormErrorNotification();
    super.ngOnDestroy();
  }

  /**
   * Sets success notication in SnackBar
   * @param message
   * @param [title]
   */
  setSuccessNotication(message: string, title?: string) {
    this.notificationService.setSuccessMessage(message, title);
  }

  /**
   * Sets error notication in SnackBar
   * @param message
   * @param [title]
   */
  setErrorNotication(message: string, title?: string) {
    this.notificationService.setErrorMessage(message, title);
  }

  /**
   * Sets error message from the back-end errors
   * @param message
   * @param [title]
   */
  setFormErrorMessage(formGroup: UntypedFormGroup) {
    if (!formGroup.valid) {
      const formErrors = getFormValidationErrors(formGroup.controls);
      const errorTitle =
        formErrors && formErrors.length > 1
          ? this.translateService.instant(
              'form_notification.form-collective-error-multiple',
              { errorCount: formErrors.length }
            )
          : this.translateService.instant(
              'form_notification.form-collective-error-single',
              { errorCount: formErrors.length }
            );

      this.notificationService.setFormErrorMessage(errorTitle, formErrors);
    } else {
      this.notificationService.clearFormErrorNotification();
    }
  }

  protected trySetPageErrorMessages(error: any, title?: string) {
    if (error instanceof BusinessRuleError || !!error?.mappedErrorList) {
      error.mappedErrorList.forEach(mappedError => {
        mappedError.value.errors.forEach(e => {
          const rppErrMsg = this.rppErrorMsg(e);
          if (!rppErrMsg) {
            this.setPageErrorMessage(
              getMessageByCode(e.code, this.translateService), title);
          } else {
            this.setPageErrorMessage(rppErrMsg.message, title || rppErrMsg.title);
          }
        });
      });

      this.scrollToTop();

    } else if (is502Error(error)){
      this.setPageErrorMessage(
        getMessageByCode('timeout_error', this.translateService), title);
    }
     else {
      throw error; // unknown error. rethrow
    }
  }

  protected getBusinessErrorCode(error: BusinessRuleError): string {
    return error?.mappedErrorList[0]?.value?.errors[0]?.code;
  }

  protected trySetErrorMessages(error: any) {
    const GENERIC_ODATA_ERR = "carm.cad.odata.error";
    const SYSTEM_ERROR = "500 Internal Server Error";

    if (error instanceof BusinessRuleError || !!error?.mappedErrorList) {

      let hasErrorCodeInMessage = false;
      let scrollToErrorMsg = false;
      let messageTitle = '';

      error.mappedErrorList.forEach(mappedError => {
        const customErrors = mappedError.value.errors
          .filter(e => e.message !== '')
          .map(e => {
            if (e.message === SYSTEM_ERROR) {
              hasErrorCodeInMessage = true;
              return this.translateService.instant('errors.generic_internal_error');
            } else if (e.message === 'carm.cad.crps.invalid.business.number.error') {
              hasErrorCodeInMessage = true;
              return this.translateService.instant('errors.invalidBusinessNumber');
            } else if (e.message === 'carm.cad.crps.invalid.cad.type.error') {
              hasErrorCodeInMessage = true;
              return this.translateService.instant('errors.invalidCadType');
            } else if (e.message === 'carm.cad.crps.invalid.work.location.error') {
              hasErrorCodeInMessage = true;
              return this.translateService.instant('errors.invalidWorkLocation');
            } else if (e.message === 'carm.cad.crps.invalid.account.security.error') {
              hasErrorCodeInMessage = true;
              return this.translateService.instant('errors.invalidAccountSecurity');
            } else if (e.message === 'carm.cad.crps.invalid.transaction.number.error') {
              hasErrorCodeInMessage = true;
              return this.translateService.instant('errors.invalidTransactionNumber');
            } else if (e.message === 'carm.cad.crps.invalid.check.digit.error') {
              hasErrorCodeInMessage = true;
              return this.translateService.instant('errors.invalidCheckDigit');
            } else if (e.message === 'carm.cad.crps.transaction.already.exist.error') {
              hasErrorCodeInMessage = true;
              return this.translateService.instant('errors.carm.cad.crps.transaction.already.exist.error');
            } else if (e.message === 'carm.cad.crps.invalid.parameter.error') {
              hasErrorCodeInMessage = true;
              return this.translateService.instant('errors.carm.cad.crps.invalid.parameter.error');
            } else if (e.message === 'carm.ddb.transaction.draft.exist.error') {
              hasErrorCodeInMessage = true;
              return this.translateService.instant('errors.carm.ddb.transaction.draft.exists.error');
            } else if (e.code === 'carm.rpp.program.enrolment.submit.create.error.message') {
              const rppMsgAndTitle = this.rppErrorMsg(e);
              hasErrorCodeInMessage = true;
              scrollToErrorMsg = true;
              messageTitle = rppMsgAndTitle.title;
              return rppMsgAndTitle.message;
            } else if (e.code === GENERIC_ODATA_ERR) { //HY-9317
              hasErrorCodeInMessage = true;
              return e.message;
            } else if ( e.message) {
              hasErrorCodeInMessage = true;
              return e.message;
            }
          });

          if(hasErrorCodeInMessage){
            this.setPageErrorMessage(customErrors.join('<br/>'), messageTitle, scrollToErrorMsg);
          }
      });

      if(!hasErrorCodeInMessage){
        throw error;
      }

    } else {
      throw error; // unknown error. rethrow
    }
  }

  private rppErrorMsg(err: any): {message: string, title: string} {
    if (err.code !== 'carm.rpp.program.enrolment.submit.create.error.message') {
      return null;
    }

    let title = "";
    let message = "";
    const caseNumber: string = err?.message.trim();
    if (!caseNumber) {
        message = this.translateService.instant('errors.carm.rpp.program.enrolment.submit.create.error.message');
    } else {
        message = this.translateService.instant('program-enrolment.failed-submission.pageShortDescriptionHyCodes', { caseNumber: caseNumber });
        title = this.translateService.instant('program-enrolment.failed-submission.pageTitle');
    }
    return { message, title };
  }

  protected createUiErrorMessage(localeKey: string, errorTitle?: string): void {
    const error = {
      mappedErrorList: [{ value: {
          errors: [{
            code: localeKey
          }] as IErrorCode[],
        } as IError,
      }] as IMappedError[],
    } as BusinessRuleError;
    this.trySetPageErrorMessages(error, errorTitle);
  }

  protected trySetPageWarningMessages(error: any) {
    if (error instanceof BusinessRuleError) {
      error.mappedErrorList.forEach(mappedError => {
        mappedError.value.errors.forEach(e => {
          this.setPageWarningMessage(
            getMessageByCode(e.code, this.translateService)
          );
        });
      });
    } else {
      throw error; // unknown error. rethrow
    }
  }

  protected trySetPageInfoMessages(error: any) {
    if (error instanceof BusinessRuleError) {
      error.mappedErrorList.forEach(mappedError => {
        mappedError.value.errors.forEach(e => {
          this.setPageInfoMessage(
            getMessageByCode(e.code, this.translateService)
          );
        });
      });
    } else {
      throw error; // unknown error. rethrow
    }
  }

  protected trySetFormErrorMessages(error: any, formGroup: UntypedFormGroup): boolean {
    if (error instanceof BusinessRuleError) {
      if (error.mappedErrorList) {
        return UpdateErrorMapInFormGroup(formGroup, error.mappedErrorList);
      }
    } else {
      throw error; // unknown error. rethrow
    }
  }

  /// Helper to try and display the page and form validation errors
  protected trySetPageAndFormErrorMessages(error: any, formGroup: UntypedFormGroup) {
    const hasFormError = this.trySetFormErrorMessages(error, formGroup);
    if (!hasFormError) {
      // only show page errors if no form errors are found
      this.trySetPageErrorMessages(error);
    }
  }

  protected downloadFile(fileContent: string, fileName?: string): void {
    const decodedBlob = atob(fileContent);
    const bytes = new Uint8Array(decodedBlob.length);
    for (let i = 0; i < decodedBlob.length; ++i) {
      bytes[i] = decodedBlob.charCodeAt(i);
    }
    const blob = new Blob([bytes], { type: 'application/pdf;base64' });

    const url = URL.createObjectURL(blob);
    window.open(url, '_blank');`y`
  }

  protected getChildrenValidationErrors(
    formGroup: UntypedFormGroup
  ): ValidationErrors {
    const allErrors: ValidationErrors = {};

    Object.keys(formGroup.controls).forEach(key => {
      const controlErrors: ValidationErrors = formGroup.get(key).errors;
      if (controlErrors != null) {
        allErrors[key] = controlErrors;
      }
    });

    return allErrors;
  }

  public tryViewSpecificValidation(
    error: any,
    viewType: string,
    viewId?: string
  ): boolean {
    let isValid;
    error.mappedErrorList.forEach(
      mappedError => {
        let errors;
        if (viewId) {
          if (viewType === 'Vendor') {
            errors = mappedError.value.errors.filter(
              err =>
                err.type === viewType &&
                err.code === 'Invoice' &&
                err.message.includes('Vendor') &&
                !err.message.includes(
                  'At least one commodity line must be non-null'
                )
            );
          } else {
            errors = mappedError.value.errors.filter(
              err =>
                err.type === viewType &&
                err.code === viewId &&
                !err.message.includes('Vendor') &&
                !err.message.includes(
                  'At least one commodity line must be non-null'
                )
            );
          }
        } else {
          errors = mappedError.value.errors.filter(
            err =>
              err.type === viewType &&
              !err.message.includes(
                'At least one commodity line must be non-null'
              )
          );
        }
        if (errors.length > 0) {
          this.setPageErrorMessage(
            errors.map(e => `${e.message}`).join('<br/>')
          );
          isValid = false;
        } else {
          isValid = true;
        }
      },
      (err: any) => {
        throw err;
      }
    );
    return isValid;
  }

  public showCloseDialog(title: string, paragraphs: string[]): Observable<boolean> {
    const dialogRef = this.dialog.open(ModalComponent, {
      data: {
        title: title,
        contentParagraphs: paragraphs,
        buttons: [
          { id: 'close', label: this.translateService.instant('button.close') },
        ]
      },
    });

    return dialogRef.afterClosed().pipe(
      takeUntil(this.destroy$),
      map((buttonId: string) => {
        return (buttonId === 'close');
      })
    );
  }

  public showConfirmDialog(
    action?: () => void,
    data?: { title?: string, termsOption?: TermOptions, certifyTitle?: string, certifyText?: string }): void {
    const dialogConfig = new MatDialogConfig();
    dialogConfig.panelClass = 'matdialog-custom-container'; // used in modal dialog component template and scss
    dialogConfig.width = '860px';
    dialogConfig.data = data;
    const dialogRef = this.dialog.open(
      ConfirmTermsDialogComponent,
      dialogConfig
    );
    dialogRef
    .afterClosed()
    .pipe(takeUntil(this.destroy$))
    .subscribe(result => {
      if (result.accept && !!action) {
        action();
      }
    });
  }

  scrollToTop(){
    window.scrollTo(0, 0)
  }

  removeHashTag = hashtag => {
    const regexp = /\#\w\w+\s?/g;
    return hashtag.replace(regexp, '');
  };

  openCustomSnackBar<T>(
    componenet: ComponentType<T>,
    config: MatSnackBarConfig,
    data: ISnackbarData
  ) : MatSnackBar {

    config.data = {...data}
    config.panelClass = 'snackbar-custom-container'; // used in snackbar component template and scss
    this.snackBar.openFromComponent(componenet, config);
    return this.snackBar;
  }
}
